import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Alert } from './Alert.vue'
export { default as AlertDescription } from './AlertDescription.vue'
export { default as AlertTitle } from './AlertTitle.vue'

/*
 * The inline callout: a note that belongs to the thing next to it and stays on
 * screen. Not the same object as a toast — the canonical Toast.jsx is the
 * transient one, a card with a 3px accent bar, and this app already raises
 * those through useNotifications.
 *
 * `tone` is the same six-name vocabulary as DSBadge and Toast.jsx, and the soft
 * fills reuse the a11y-corrected `-subtle` pairs: the canonical pairing of a
 * 600-step foreground on a 50-step wash fails AA for green and amber.
 *
 * Reconciled from the registry, which ships `default | destructive`, a
 * `rounded-lg` box at `p-4`, `tracking-tight`, and a leading icon positioned
 * absolutely with a `pl-7` reservation on its siblings. The icon here is a flex
 * child instead, which is what every callout in this app already draws and what
 * survives a title wrapping to two lines.
 */
export const alertVariants = cva(
  [
    'relative flex w-full items-start gap-2.5',
    'px-3.5 py-3 rounded-ds-md font-ds',
    '[&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:mt-0.5',
  ].join(' '),
  {
    variants: {
      tone: {
        brand: 'bg-accent text-accent-foreground',
        neutral: 'bg-secondary text-secondary-foreground',
        success: 'bg-success-subtle text-success-subtle-foreground',
        warning: 'bg-warning-subtle text-warning-subtle-foreground',
        danger: 'bg-destructive-subtle text-destructive-subtle-foreground',
        info: 'bg-info-subtle text-info-subtle-foreground',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
)

export type AlertVariants = VariantProps<typeof alertVariants>
